<script module>
  import AppLayout from '../../layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script>
  import { router } from '@inertiajs/svelte'
  import { Button } from '$lib/components/ui/button/index.js'
  import PageContainer from '$lib/components/page-container.svelte'
  import { Input } from '$lib/components/ui/input/index.js'
  import { Label } from '$lib/components/ui/label/index.js'
  import { Progress } from '$lib/components/ui/progress/index.js'
  import { Alert, AlertDescription } from '$lib/components/ui/alert/index.js'
  import { Card, CardHeader, CardTitle, CardContent, CardAction } from '$lib/components/ui/card/index.js'
  import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SheetDescription
  } from '$lib/components/ui/sheet/index.js'
  import * as Dialog from '$lib/components/ui/dialog/index.js'
  import PencilIcon from '@lucide/svelte/icons/pencil'
  import PlusIcon from '@lucide/svelte/icons/plus'
  import TrashIcon from '@lucide/svelte/icons/trash-2'
  import XIcon from '@lucide/svelte/icons/x'

  let { property, custom_expenses = [], errors = {} } = $props()

  const UTILITIES = [
    { field: 'electricity_annual_cents', cardTitle: 'Electricity', label: 'Electricity', helper: 'Enter the annual electricity bill amount.' },
    { field: 'water_annual_cents',       cardTitle: 'Water',       label: 'Water',       helper: 'Enter the annual water bill amount.' },
    { field: 'sewerage_annual_cents',    cardTitle: 'Sewerage (Indah Water)',    label: 'Sewerage',    helper: 'Enter the annual sewerage charges.' },
  ]

  const FREQUENCIES = [
    { value: 'weekly',   label: 'Weekly (×52)' },
    { value: 'biweekly', label: 'Biweekly (×26)' },
    { value: 'monthly',  label: 'Monthly (×12)' },
  ]

  // ── Utility mobile sheet state ────────────────────────────────────────
  let utilitySheetOpen = $state(false)
  let activeUtility = $state(null)
  let utilityAmountRm = $state('')
  let utilitySubmitting = $state(false)

  // ── Utility desktop inline state ──────────────────────────────────────
  let localUtilityAmounts = $state(
    Object.fromEntries(UTILITIES.map(u => [u.field, (property[u.field] / 100).toFixed(2)]))
  )
  let savingUtilityField = $state(null)

  function openUtilitySheet(utility) {
    activeUtility = utility
    utilityAmountRm = (property[utility.field] / 100).toFixed(2)
    utilitySheetOpen = true
  }

  function handleUtilitySave() {
    if (utilitySubmitting) return
    utilitySubmitting = true
    const cents = Math.round(parseFloat(utilityAmountRm || '0') * 100)
    router.put(`/properties/${property.id}/utilities`, {
      utilities: { field: activeUtility.field, amount_cents: cents }
    }, {
      preserveState: false,
      onSuccess: () => { utilitySheetOpen = false },
      onFinish: () => { utilitySubmitting = false }
    })
  }

  function handleUtilityBlur(utility) {
    const newCents = Math.round(parseFloat(localUtilityAmounts[utility.field] || '0') * 100)
    if (newCents === property[utility.field]) return
    savingUtilityField = utility.field
    router.put(`/properties/${property.id}/utilities`, {
      utilities: { field: utility.field, amount_cents: newCents }
    }, {
      preserveState: true,
      preserveScroll: true,
      viewTransition: false,
      onFinish: () => { savingUtilityField = null }
    })
  }

  // ── Custom expense shared form state ──────────────────────────────────
  let expenseSheetOpen = $state(false)   // mobile
  let expenseDialogOpen = $state(false)  // desktop
  let editingExpense = $state(null)      // null = create mode
  let expenseName = $state('')
  let expenseType = $state('one_time')
  let expenseAmountRm = $state('')
  let expenseOccurrences = $state(1)
  let expenseFrequency = $state('monthly')
  let expenseSubmitting = $state(false)

  function resetExpenseForm() {
    editingExpense = null
    expenseName = ''
    expenseType = 'one_time'
    expenseAmountRm = ''
    expenseOccurrences = 1
    expenseFrequency = 'monthly'
  }

  function openAddExpense() {
    resetExpenseForm()
    if (window.matchMedia('(min-width: 640px)').matches) {
      expenseDialogOpen = true
    } else {
      expenseSheetOpen = true
    }
  }

  function openEditExpense(expense) {
    editingExpense = expense
    expenseName = expense.name
    expenseType = expense.expense_type
    expenseAmountRm = (expense.amount_cents / 100).toFixed(2)
    expenseOccurrences = expense.occurrences
    expenseFrequency = expense.frequency || 'monthly'
    if (window.matchMedia('(min-width: 640px)').matches) {
      expenseDialogOpen = true
    } else {
      expenseSheetOpen = true
    }
  }

  function closeExpenseModal() {
    expenseSheetOpen = false
    expenseDialogOpen = false
  }

  function handleExpenseSave() {
    if (expenseSubmitting) return
    expenseSubmitting = true
    const cents = Math.round(parseFloat(expenseAmountRm || '0') * 100)
    const payload = {
      custom_expense: {
        name: expenseName,
        expense_type: expenseType,
        amount_cents: cents,
        occurrences: expenseType === 'one_time' ? Number(expenseOccurrences) : 1,
        frequency: expenseType === 'recurring' ? expenseFrequency : null,
      }
    }

    if (editingExpense) {
      router.put(`/properties/${property.id}/custom_expenses/${editingExpense.id}`, payload, {
        preserveState: false,
        onSuccess: closeExpenseModal,
        onFinish: () => { expenseSubmitting = false }
      })
    } else {
      router.post(`/properties/${property.id}/custom_expenses`, payload, {
        preserveState: false,
        onSuccess: closeExpenseModal,
        onFinish: () => { expenseSubmitting = false }
      })
    }
  }

  function handleExpenseDelete(expense) {
    if (!confirm(`Delete "${expense.name}"?`)) return
    router.delete(`/properties/${property.id}/custom_expenses/${expense.id}`, {
      preserveState: false
    })
  }

  function formatRM(cents) {
    return (cents / 100).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  function frequencyLabel(freq) {
    return FREQUENCIES.find(f => f.value === freq)?.label ?? freq
  }
</script>

<svelte:head>
  <title>Utilities & Custom — MyRentalTax</title>
</svelte:head>

<PageContainer>
  {#snippet breadcrumb()}
    <span class="text-[11px] font-medium text-muted-foreground">Utilities & Custom · Step 4 of 5</span>
  {/snippet}
    <!-- Header -->
    <div class="flex flex-col gap-1">
      <h1 class="text-xl sm:text-2xl font-semibold text-foreground m-0">Utilities & Custom Expenses</h1>
      <p class="text-sm text-muted-foreground m-0 leading-relaxed">Enter your utility bills and any additional recurring or one-time expenses. These are tax-deductible against rental income.</p>
    </div>

    <!-- Progress row (desktop) -->
    <div class="hidden sm:flex items-center gap-3">
      <span class="text-xs font-medium text-muted-foreground">Step 4 of 5</span>
      <Progress value={4} max={5} class="flex-1" />
    </div>

    <!-- Utility cards — 3-column grid on desktop -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {#each UTILITIES as utility}
        <Card class="shadow-none border-border">
          <CardHeader class="px-4 sm:px-6 py-2 sm:py-3">
            <CardTitle class="text-sm font-semibold">{utility.cardTitle}</CardTitle>
            <!-- Pencil edit button — mobile only -->
            <CardAction class="sm:hidden">
              <Button
                variant="ghost"
                size="icon"
                class="h-7 w-7 text-muted-foreground"
                onclick={() => openUtilitySheet(utility)}
                aria-label="Edit {utility.label}"
              >
                <PencilIcon class="w-4 h-4" />
              </Button>
            </CardAction>
          </CardHeader>

          <!-- Desktop: inline editable input -->
          <div class="hidden sm:block px-6 py-3">
            <div class="flex flex-col gap-1.5">
              <Label for="utility-inline-{utility.field}">Annual Total (RM)</Label>
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">RM</span>
                <Input
                  id="utility-inline-{utility.field}"
                  type="number"
                  min="0"
                  step="0.01"
                  bind:value={localUtilityAmounts[utility.field]}
                  class="h-10 pl-10"
                  disabled={savingUtilityField === utility.field}
                  onblur={() => handleUtilityBlur(utility)}
                />
              </div>
              {#if savingUtilityField === utility.field}
                <p class="text-xs text-muted-foreground m-0">Saving…</p>
              {/if}
            </div>
          </div>

          <!-- Mobile: value display -->
          <CardContent class="sm:hidden px-4 py-4 flex flex-col gap-1">
            <p class="text-xs text-muted-foreground m-0">{utility.label}</p>
            <p class="text-xs text-muted-foreground m-0 mt-1">Annual Amount (RM)</p>
            <p class="text-base font-semibold text-foreground m-0">{formatRM(property[utility.field])}</p>
          </CardContent>
        </Card>
      {/each}
    </div>

    <!-- Custom expenses section -->
    <div class="flex flex-col gap-3">
      <div class="flex items-center justify-between">
        <h2 class="text-base font-semibold text-foreground m-0">Custom Expenses</h2>
        <Button variant="outline" size="sm" onclick={openAddExpense}>
          <PlusIcon class="w-4 h-4 mr-1" />
          Add Expense
        </Button>
      </div>

      {#if custom_expenses.length === 0}
        <p class="text-sm text-muted-foreground">No custom expenses yet. Add repairs, management fees, or other deductible costs.</p>
      {:else}
        <div class="flex flex-col gap-2">
          {#each custom_expenses as expense (expense.id)}
            <Card class="shadow-none border-border">
              <CardContent class="px-4 py-3 flex items-center gap-3">
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-foreground m-0 truncate">{expense.name}</p>
                  <p class="text-xs text-muted-foreground m-0 mt-0.5">
                    {#if expense.expense_type === 'one_time'}
                      One-time{expense.occurrences > 1 ? ` × ${expense.occurrences}` : ''} · RM {formatRM(expense.annual_total_cents)}/yr
                    {:else}
                      {frequencyLabel(expense.frequency)} · RM {formatRM(expense.annual_total_cents)}/yr
                    {/if}
                  </p>
                </div>
                <div class="flex items-center gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-7 w-7 text-muted-foreground"
                    onclick={() => openEditExpense(expense)}
                    aria-label="Edit {expense.name}"
                  >
                    <PencilIcon class="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onclick={() => handleExpenseDelete(expense)}
                    aria-label="Delete {expense.name}"
                  >
                    <TrashIcon class="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          {/each}
        </div>
      {/if}
    </div>

  {#snippet footer()}
    <Button variant="outline" onclick={() => router.visit(`/properties/${property.id}/statutory_taxes`)}>Back</Button>
    <div class="flex-1"></div>
    <Button onclick={() => router.visit(`/properties/${property.id}/summary`)}>Next: Review & Summary →</Button>
  {/snippet}
</PageContainer>

<!-- ── Custom expense form snippet (shared between dialog and sheet) ─── -->
{#snippet expenseFormBody()}
  <!-- Name -->
  <div class="flex flex-col gap-1.5">
    <Label for="expense-name">Expense Name</Label>
    <Input id="expense-name" type="text" placeholder="e.g. Repairs, Management Fee" bind:value={expenseName} class="h-10" />
  </div>

  <!-- Type toggle -->
  <div class="flex flex-col gap-1.5">
    <Label>Type</Label>
    <div class="flex rounded-md border border-input overflow-hidden">
      <button
        class="flex-1 py-2 text-sm font-medium transition-colors {expenseType === 'one_time' ? 'bg-foreground text-background' : 'bg-background text-muted-foreground hover:text-foreground'}"
        onclick={() => { expenseType = 'one_time' }}
        type="button"
      >One-time</button>
      <button
        class="flex-1 py-2 text-sm font-medium transition-colors {expenseType === 'recurring' ? 'bg-foreground text-background' : 'bg-background text-muted-foreground hover:text-foreground'}"
        onclick={() => { expenseType = 'recurring' }}
        type="button"
      >Recurring</button>
    </div>
  </div>

  <!-- Amount -->
  <div class="flex flex-col gap-1.5">
    <Label for="expense-amount">Amount per {expenseType === 'one_time' ? 'occurrence' : 'period'} (RM)</Label>
    <div class="relative">
      <span class="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">RM</span>
      <Input
        id="expense-amount"
        type="number"
        min="0"
        step="0.01"
        bind:value={expenseAmountRm}
        class="h-10 pl-10"
      />
    </div>
  </div>

  <!-- One-time: occurrences -->
  {#if expenseType === 'one_time'}
    <div class="flex flex-col gap-1.5">
      <Label for="expense-occurrences">Number of occurrences this year</Label>
      <Input id="expense-occurrences" type="number" min="1" step="1" bind:value={expenseOccurrences} class="h-10" />
    </div>
  {/if}

  <!-- Recurring: frequency -->
  {#if expenseType === 'recurring'}
    <div class="flex flex-col gap-1.5">
      <Label>Frequency</Label>
      <div class="flex flex-col gap-2">
        {#each FREQUENCIES as freq}
          <label class="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="expense-frequency"
              value={freq.value}
              bind:group={expenseFrequency}
              class="w-4 h-4 accent-foreground"
            />
            <span class="text-sm">{freq.label}</span>
          </label>
        {/each}
      </div>
    </div>
  {/if}

  {#if errors.name}
    <Alert variant="destructive">
      <AlertDescription>{errors.name[0]}</AlertDescription>
    </Alert>
  {/if}
{/snippet}

<!-- ── Desktop: Dialog ────────────────────────────────────────────────── -->
<Dialog.Root bind:open={expenseDialogOpen}>
  <Dialog.Content class="p-0 gap-0">
    <Dialog.Header class="px-5 py-4 border-b border-border">
      <Dialog.Title>{editingExpense ? 'Edit Expense' : 'Add Custom Expense'}</Dialog.Title>
      <Dialog.Description class="sr-only">Enter the details for your custom expense.</Dialog.Description>
    </Dialog.Header>

    <div class="px-5 py-5 flex flex-col gap-4">
      {@render expenseFormBody()}
    </div>

    <Dialog.Footer class="px-5 py-4 border-t border-border sm:flex-row gap-3">
      <Button variant="outline" onclick={closeExpenseModal} class="flex-shrink-0">Cancel</Button>
      <Button onclick={handleExpenseSave} disabled={expenseSubmitting} class="sm:flex-1">
        {expenseSubmitting ? 'Saving…' : (editingExpense ? 'Save Changes' : 'Add Expense')}
      </Button>
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>

<!-- ── Mobile: Sheet ──────────────────────────────────────────────────── -->
<Sheet bind:open={expenseSheetOpen}>
  <SheetContent side="bottom" class="p-0 rounded-t-2xl" showCloseButton={false}>
    <!-- Drag handle -->
    <div class="flex justify-center pt-2">
      <div class="w-9 h-1 rounded-full bg-muted-foreground opacity-40"></div>
    </div>

    <SheetHeader class="flex-row items-center justify-between px-4 py-3 border-b border-border gap-0.5">
      <SheetTitle>{editingExpense ? 'Edit Expense' : 'Add Custom Expense'}</SheetTitle>
      <SheetDescription class="sr-only">Enter the details for your custom expense.</SheetDescription>
      <Button variant="ghost" size="icon" class="h-6 w-6 text-muted-foreground -mr-1" onclick={closeExpenseModal} aria-label="Close">
        <XIcon class="w-4 h-4" />
      </Button>
    </SheetHeader>

    <div class="px-4 py-5 flex flex-col gap-4">
      {@render expenseFormBody()}
    </div>

    <SheetFooter class="flex-row px-4 pb-6 pt-0 gap-3 border-t border-border">
      <Button variant="outline" onclick={closeExpenseModal} class="flex-shrink-0">Cancel</Button>
      <Button onclick={handleExpenseSave} disabled={expenseSubmitting} class="flex-1">
        {expenseSubmitting ? 'Saving…' : (editingExpense ? 'Save Changes' : 'Add Expense')}
      </Button>
    </SheetFooter>
  </SheetContent>
</Sheet>

<!-- Edit utility sheet (mobile only) -->
<Sheet bind:open={utilitySheetOpen}>
  <SheetContent side="bottom" class="p-0 rounded-t-2xl" showCloseButton={false}>
    <!-- Drag handle -->
    <div class="flex justify-center pt-2">
      <div class="w-9 h-1 rounded-full bg-muted-foreground opacity-40"></div>
    </div>

    <SheetHeader class="flex-row items-center justify-between px-4 py-3 border-b border-border gap-0.5">
      <SheetTitle>{activeUtility?.label ?? ''}</SheetTitle>
      <SheetDescription class="sr-only">{activeUtility?.helper ?? ''}</SheetDescription>
      <Button variant="ghost" size="icon" class="h-6 w-6 text-muted-foreground -mr-1" onclick={() => { utilitySheetOpen = false }} aria-label="Close">
        <XIcon class="w-4 h-4" />
      </Button>
    </SheetHeader>

    <div class="px-4 py-5 flex flex-col gap-3">
      <div class="flex flex-col gap-1.5">
        <Label for="utility-input">Annual Amount (RM)</Label>
        <div class="relative">
          <span class="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">RM</span>
          <Input
            id="utility-input"
            type="number"
            min="0"
            step="0.01"
            bind:value={utilityAmountRm}
            class="h-10 pl-10"
          />
        </div>
        {#if activeUtility}
          <p class="text-xs text-muted-foreground m-0">{activeUtility.helper}</p>
        {/if}
      </div>
    </div>

    <SheetFooter class="flex-row px-4 pb-6 pt-0 gap-3 border-t border-border">
      <Button variant="outline" onclick={() => { utilitySheetOpen = false }} class="flex-shrink-0">Cancel</Button>
      <Button onclick={handleUtilitySave} disabled={utilitySubmitting} class="flex-1">
        {utilitySubmitting ? 'Saving…' : 'Save'}
      </Button>
    </SheetFooter>
  </SheetContent>
</Sheet>
